import React from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';

/**
 * Composant de bouton réutilisable pour l'administration
 * @param {Object} props - Les propriétés du composant
 * @param {String} props.type - Type de bouton (primary, secondary, outline)
 * @param {String} props.to - URL de destination (pour les liens)
 * @param {Function} props.onClick - Fonction appelée lors du clic
 * @param {String} props.icon - Nom de l'icône à afficher
 * @param {Boolean} props.disabled - Désactiver le bouton
 * @param {String} props.className - Classes CSS supplémentaires
 * @param {React.ReactNode} props.children - Contenu du bouton
 */
const AdminButton = ({ 
  type = 'primary', 
  to, 
  onClick, 
  icon, 
  disabled = false, 
  className = '',
  children 
}) => {
  // Classes de base pour tous les boutons
  const baseClasses = 'btn-modern';
  
  // Classes spécifiques selon le type de bouton
  const typeClasses = {
    primary: 'btn-primary-modern',
    secondary: 'btn-secondary-modern',
    outline: 'btn-outline-modern',
    danger: 'btn-danger-modern'
  };
  
  // Classe finale du bouton
  const buttonClass = `${baseClasses} ${typeClasses[type] || typeClasses.primary} ${className}`;
  
  // Rendu de l'icône si spécifiée
  const renderIcon = () => {
    if (!icon) return null;
    
    const icons = {
      add: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      ),
      edit: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      ),
      delete: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      ),
      back: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
      ),
      save: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
        </svg>
      )
    };
    
    return icons[icon] || null;
  };
  
  // Si c'est un lien
  if (to) {
    return (
      <Link to={to} className={buttonClass} disabled={disabled}>
        {renderIcon()}
        {children}
      </Link>
    );
  }
  
  // Sinon c'est un bouton
  return (
    <button onClick={onClick} className={buttonClass} disabled={disabled}>
      {renderIcon()}
      {children}
    </button>
  );
};

AdminButton.propTypes = {
  type: PropTypes.oneOf(['primary', 'secondary', 'outline', 'danger']),
  to: PropTypes.string,
  onClick: PropTypes.func,
  icon: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node
};

export default AdminButton;
